import { getStoreCurrency } from '@jbhi-fi/utils/src/shopify/getStoreCurrency';
import { Tracker } from '../Tracker';
import { TrackingDescriptorType } from '../types';

export interface SubmitTrackingProps {
  event: string;
  action?: string;
  trackingDescriptor: TrackingDescriptorType;
  gtm: any;
}

export const categoryStringToDimensions = (
  category: string
): { dimension27: string; dimension28: string; dimension29: string } => {
  if (category) {
    const categories = category.split('/');
    return {
      dimension27: categories[0]?.trim(),
      dimension28: categories[1]?.trim() || '(not set)',
      dimension29: categories[2]?.trim() || '(not set)',
    };
  } else {
    return {
      dimension27: '(not set)',
      dimension28: '(not set)',
      dimension29: '(not set)',
    };
  }
};

export const transformGtmProps = ({
  gtm,
  ecommerceExtraProps,
}: {
  gtm: {
    category: string;
    list?: string;
    position?: number;
    quantity: number;
    price: number;
    dimension4?: string;
    dimension6?: string;
    productsList?: string;
  };
  ecommerceExtraProps?: Record<string, unknown>;
}) => {
  if (!gtm) return;
  const list = gtm.list ?? ecommerceExtraProps?.list ?? '(not set)';
  const position = gtm.position ?? ecommerceExtraProps?.position ?? '(not set)';
  const quantity = gtm.quantity ?? ecommerceExtraProps?.quantity ?? 1;
  const metric1 = gtm.price ?? ecommerceExtraProps?.price ?? '(not set)';
  const dimension4 = gtm.dimension4 || '(not set)';
  const dimension6 = gtm.dimension6 || '(not set)';
  const productsList = gtm.productsList || list || '(not set)';
  return {
    ...gtm,
    ...ecommerceExtraProps,
    list,
    position,
    quantity,
    metric1,
    productsList,
    dimension4,
    dimension6,
    ...categoryStringToDimensions(gtm?.category),
  };
};

export const submitTracking = ({ event, action, trackingDescriptor, gtm }: SubmitTrackingProps) => {
  const { page, group, category, ecommerceExtraProps } = trackingDescriptor;

  const trackingObj = {
    event,
    action,
    group,
    category,
    pageType: page,
    location: page,
    ecommerce: {},
  };

  let transformedGtm;
  if (Array.isArray(gtm)) {
    transformedGtm = gtm.map(gtmItem => transformGtmProps({ gtm: gtmItem, ecommerceExtraProps }));
  } else {
    transformedGtm = [transformGtmProps({ gtm, ecommerceExtraProps })];
  }

  if (event === 'addToCart') {
    trackingObj.ecommerce = {
      currencyCode: getStoreCurrency(),
      add: {
        actionField: {
          list: ecommerceExtraProps?.list,
        },
        products: transformedGtm,
      },
    };
  } else if (event === 'productClick') {
    trackingObj.ecommerce = {
      currencyCode: getStoreCurrency(),
      click: {
        actionField: {
          list: ecommerceExtraProps?.list,
        },
        products: transformedGtm,
      },
    };
  } else if (event === 'productImpression') {
    trackingObj.ecommerce = {
      currencyCode: getStoreCurrency(),
      impressions: transformedGtm,
    };
  } else {
    trackingObj.ecommerce = {
      products: transformedGtm,
    };
  }
  Tracker.trackCustomEvent(trackingObj);
};
